interface AICol {
  title: string;
  items: string[];
}

interface InnerAIAnswerBlockProps {
  h2: string;
  intro: string;
  col1: AICol;
  col2: AICol;
  col3Items?: { step: string; label: string; desc: string; }[];
}

export default function InnerAIAnswerBlock({ h2, intro, col1, col2, col3Items }: InnerAIAnswerBlockProps) {
  const defaultProcess = [
    { step: '01', label: 'Free Consultation', desc: 'No-cost conversation about your needs' },
    { step: '02', label: 'Custom Care Plan', desc: 'Tailored specifically for your loved one' },
    { step: '03', label: 'Caregiver Match', desc: 'Carefully selected for compatibility' },
    { step: '04', label: 'Ongoing Support', desc: 'Regular check-ins and adjustments' },
  ];
  const processItems = col3Items ?? defaultProcess;

  return (
    <section
      className="py-28 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #0c2236 0%, #0d3528 40%, #0F2A44 70%, #0c2236 100%)' }}
    >
      <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full border border-white/[0.04] pointer-events-none" />
      <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full border border-white/[0.04] pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-full h-full pointer-events-none opacity-25"
        style={{ backgroundImage: 'radial-gradient(ellipse at 75% 40%, rgba(200,169,106,0.07) 0%, transparent 55%)' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="inline-block w-10 h-px bg-gold opacity-60"></span>
            <p className="text-gold text-xs font-bold uppercase tracking-[0.2em]">About This Service</p>
            <span className="inline-block w-10 h-px bg-gold opacity-60"></span>
          </div>
          <h2 className="font-heading text-white font-bold mb-5 leading-tight" style={{ fontSize: 'clamp(1.9rem, 3.5vw, 3.2rem)' }}>
            {h2}
          </h2>
          <p className="text-white/55 text-base leading-relaxed max-w-3xl mx-auto">{intro}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Column 1 */}
          <div
            className="rounded-2xl p-8 border border-white/[0.08] relative overflow-hidden hover:border-white/15 transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)' }}
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-gold/[0.04] rounded-full translate-x-8 -translate-y-8 pointer-events-none" />
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gold/20">
                <i className="ri-service-line text-gold text-lg"></i>
              </div>
              <h3 className="font-heading text-white text-xl font-bold">{col1.title}</h3>
            </div>
            <ul className="space-y-3.5">
              {col1.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-white/65 text-sm leading-relaxed">
                  <i className="ri-checkbox-circle-fill text-sage mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 2 */}
          <div
            className="rounded-2xl p-8 border border-white/[0.08] relative overflow-hidden hover:border-white/15 transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)' }}
          >
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-sage/[0.04] rounded-full -translate-x-8 translate-y-8 pointer-events-none" />
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gold/20">
                <i className="ri-heart-3-fill text-gold text-lg"></i>
              </div>
              <h3 className="font-heading text-white text-xl font-bold">{col2.title}</h3>
            </div>
            <ul className="space-y-3.5">
              {col2.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-white/65 text-sm leading-relaxed">
                  <i className="ri-checkbox-circle-fill text-sage mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Process */}
          <div
            className="rounded-2xl p-8 border border-white/[0.08] relative overflow-hidden hover:border-white/15 transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)' }}
          >
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gold/20">
                <i className="ri-route-fill text-gold text-lg"></i>
              </div>
              <h3 className="font-heading text-white text-xl font-bold">Our Process</h3>
            </div>
            <div className="relative">
              <div className="absolute left-4 top-4 bottom-4 w-px bg-white/10" />
              <div className="space-y-5">
                {processItems.map((item) => (
                  <div key={item.step} className="flex items-start gap-4 pl-1">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0F2A44] border-2 border-gold/50 flex-shrink-0 z-10 relative">
                      <span className="font-heading text-gold text-[10px] font-bold">{item.step}</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold leading-snug">{item.label}</p>
                      <p className="text-white/40 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
